import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  #pointerDownInside = false;

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** Tracks whether the results panel is intentionally open. */
  #panelOpen = false;

  /** Cached empty-state section markup so sectionRenderer is only called once. */
  /** @type {string | null} */
  #emptySectionMarkupCache = null;

  /**
   * Set to true when the panel opens via focus triggered by an outside pointer interaction.
   * Cleared after one animation frame so the subsequent click event on that same outside element
   * does not immediately close a panel that was just opened.
   */
  #panelJustOpenedFromFocus = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }
    document.addEventListener('click', this.#handleOutsideClick, { signal });
    // Some draggable sections suppress click events; pointerdown ensures outside interactions still close.
    document.addEventListener('pointerdown', this.#handleOutsidePointerDown, { signal, capture: true });
    document.addEventListener('scroll', this.#handleScroll, { signal, passive: true, capture: true });

    this.refs.searchInput.addEventListener('focus', this.#handleSearchInputFocus, { signal });

    // Track pointer interactions inside the component. Some browsers don't set FocusEvent.relatedTarget
    // on blur when clicking a result, which can cause focusout-driven closing to remove results
    // before the click navigates. We use this to avoid closing on such clicks.
    this.addEventListener('pointerdown', this.#handlePointerDown, { signal, capture: true });
    document.addEventListener('pointerup', this.#handlePointerUp, { signal });

    // If focus leaves the whole predictive-search component and the input is empty,
    // hide the dropdown immediately so the empty/recently-viewed panel doesn't block other UI.
    this.addEventListener('focusout', this.#handleFocusOut, { signal });

    if (dialog && RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#panelOpen = false;
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded) {
      this.#loadEmptyState();
    }
  };

  #handleSearchInputFocus = () => {
    this.#panelOpen = true;
    // Guard against the click event that triggered this focus from immediately re-closing the panel.
    // rAF fires after all pending events in the current interaction have dispatched.
    this.#panelJustOpenedFromFocus = true;
    requestAnimationFrame(() => { this.#panelJustOpenedFromFocus = false; });
    // Restore display — panel was hidden without clearing content, so existing content shows immediately.
    this.refs.predictiveSearchResults.style.display = '';
    if (this.refs.searchInput.value.trim().length > 0) return;
    this.#loadEmptyState();
  };

  /**
   * Close predictive results when clicking outside the component.
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    const target = /** @type {Node | null} */ (event.target);
    if (!target || this.contains(target)) return;
    if (!this.#panelOpen) return;
    // Skip if the panel was just opened by a focus event caused by this same click sequence.
    if (this.#panelJustOpenedFromFocus) return;
    this.#closeResultsPanel();
  };

  /**
   * Close predictive results on pointerdown outside the component.
   * This catches cases where click is prevented by draggable UI elements.
   * @param {PointerEvent} event
   */
  #handleOutsidePointerDown = (event) => {
    const target = /** @type {Node | null} */ (event.target);
    if (!target || this.contains(target)) return;
    if (!this.#panelOpen) return;
    this.#closeResultsPanel();
  };

  /**
   * Hide predictive results and reset panel state.
   * @param {boolean} [clearInput=true]
   */
  #closeResultsPanel(clearInput = true) {
    this.#panelOpen = false;
    // Abort any in-flight fetch so it cannot re-render the panel after we close it.
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#currentIndex = -1;
    // Hide synchronously without clearing content — works for both the [data-search-results]
    // state and the recently viewed empty state (which has no [data-search-results] attribute),
    // and bypasses slideshow drag-handling that may suppress or delay pointer/click events.
    // Preserving content means no re-fetch is needed when the panel reopens.
    this.refs.predictiveSearchResults.style.display = 'none';
    if (clearInput) {
      this.refs.searchInput.value = '';
      this.#hideResetButton();
      // Only reset when input is cleared so recently viewed reloads fresh on next outside-click close.
      this.#emptyStateLoaded = false;
    }
  }

  /**
   * Close recently viewed/results when the page scrolls after search focus has moved away.
   * This prevents the empty state panel from covering page content.
   */
  /** @param {Event} event */
  #handleScroll = (event) => {
    const target = /** @type {Node | null} */ (event.target);
    // Allow scrolling inside the predictive search panel without dismissing it.
    if (target && this.contains(target)) return;
    // Ignore scroll events from sibling components (slideshow, carousel, etc.).
    // Only close when an ancestor scrolls — that means the user is scrolling the page itself.
    if (target && target !== document && !/** @type {Element} */ (target).contains(this)) return;
    if (!this.#panelOpen) return;
    this.#closeResultsPanel(false);
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    const target = /** @type {Node | null} */ (event.target);
    if (!target) return;
    this.#pointerDownInside = this.contains(target);
  };

  #handlePointerUp = () => {
    this.#pointerDownInside = false;
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    // If focus is still within the component (e.g. tabbing into results), do nothing.
    if (next && this.contains(next)) return;

    // If the user is clicking inside the component, do not close on focusout.
    // This avoids removing results before a click on a product link completes.
    if (this.#pointerDownInside) return;

    const searchTerm = this.refs.searchInput.value.trim();
    if (searchTerm.length > 0) return;

    // Reset results panel only (don't clear input — it's already empty)
    this.#closeResultsPanel(false);
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    if (index >= 0) {
      this.refs.searchInput.focus();
    }
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Closes the predictive search results panel without clearing the input or recently viewed list.
   * @param {Event} event - The event.
   */
  closePredictiveSearch(event) {
    event.stopPropagation();
    // Mark empty state as stale so the next focus always triggers a fresh render.
    // This is the primary guard: even if the in-flight fetch below gets aborted
    // by the focusout → #closeResultsPanel that fires when the X button loses
    // focus, the re-focus will still kick off a new #resetSearch and render
    // the recently viewed content correctly.
    this.#emptyStateLoaded = false;
    // Also prevent the immediate abort inside #closeResultsPanel so the
    // in-flight #resetSearch has a chance to finish and pre-populate the DOM
    // before the user re-focuses (best-case: content appears with no delay).
    const savedFetch = this.#activeFetch;
    this.#activeFetch = null;
    this.#closeResultsPanel(false);
    this.#activeFetch = savedFetch;
    // For inline (non-dialog) mode blur the input so the CSS-driven wrapper hides.
    if (!this.dialog) {
      this.refs.searchInput.blur();
    }
  }

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    // Require at least 2 characters before querying predictive search.
    // Do not call full #resetSearch() here — that clears the input; users pausing on 1 character
    // would lose what they typed. Only reset the results panel back to the empty state.
    if (searchTerm.length < 2) {
      void this.#resetSearch(false);
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 300);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;
    this.#panelOpen = true;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // Include SKU (and barcode) in predictive search matching.
    // Shopify defaults predictive search fields to title/product_type/variants.title/vendor unless overridden.
    // Docs: https://shopify.dev/docs/api/ajax/reference/predictive-search
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor,variants.sku,variants.barcode'
    );

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);
        predictiveSearchResults.style.display = '';

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * @param {boolean} [clearInput=true] - When false, only the results markup is reset (e.g. query under 2 chars).
   *   The input value is kept so typing is not erased on debounce.
   */
  #resetSearch = async (clearInput = true) => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    if (clearInput) {
      searchInput.value = '';
      this.#hideResetButton();
    } else if (searchInput.value.trim().length > 0) {
      this.#showResetButton();
    } else {
      this.#hideResetButton();
    }

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    // Cache the empty-section markup after the first successful fetch — sectionRenderer
    // can return null on subsequent calls after an abort, causing a false error.
    if (!this.#emptySectionMarkupCache) {
      const fetched = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
      if (fetched) this.#emptySectionMarkupCache = fetched;
    }

    const parsedEmptySectionMarkup = this.#emptySectionMarkupCache
      ? new DOMParser()
          .parseFromString(this.#emptySectionMarkupCache, 'text/html')
          .querySelector('.predictive-search-empty-section')
      : null;

    if (!parsedEmptySectionMarkup) return;

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();

    // morph may remove inline display:none set by #closeResultsPanel — re-apply if still closed.
    if (!this.#panelOpen) {
      predictiveSearchResults.style.display = 'none';
      this.#emptyStateLoaded = false;
    } else {
      predictiveSearchResults.style.display = '';
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
